import React, { ImgHTMLAttributes, useState } from 'react';

type LazyImageProps = ImgHTMLAttributes<HTMLImageElement> & {
    /** Optional CSS class(es) for the wrapper div. */
    wrapperClassName?: string;
    /** Optional inline style for the wrapper div. */
    wrapperStyle?: React.CSSProperties;
    /** Placeholder width in pixels when no explicit size is provided. Default: 1539 */
    placeholderWidth?: number;
    /** Placeholder height in pixels when no explicit size is provided. Default: 900 */
    placeholderHeight?: number;
};

/**
 * Drop-in replacement for <img> that prevents layout shift during loading.
 * The wrapper maintains stable dimensions while the image fetches, occupying
 * the same space the loaded image will occupy. A spinning circle is shown
 * as a placeholder until the image is ready.
 */
export default function LazyImage({
    className,
    style,
    wrapperClassName,
    wrapperStyle,
    onLoad,
    onError,
    src,
    alt,
    placeholderWidth = 500,
    placeholderHeight = 400,
    ...imgProps
}: LazyImageProps) {
    const [loaded, setLoaded] = useState(false);
    // Track the last rendered src so we can reset `loaded` synchronously when it changes.
    // Using state-during-render (instead of useEffect) avoids a one-frame flash of the stale image.
    const [lastSrc, setLastSrc] = useState(src);
    if (src !== lastSrc) {
        setLastSrc(src);
        setLoaded(false);
    }

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        setLoaded(true);
        onLoad?.(e);
    };

    // On error, reveal the broken-image state instead of showing the spinner forever
    const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        setLoaded(true);
        onError?.(e);
    };

    const toCssLength = (v: string | number | undefined, fallbackPx: number) =>
        v == null ? `${fallbackPx}px` : typeof v === 'number' ? `${v}px` : v;

    return (
        <div
            className={`lazy-image-wrapper${wrapperClassName ? ` ${wrapperClassName}` : ''}`}
            style={{
                '--img-width': toCssLength(wrapperStyle?.width ?? style?.width, placeholderWidth),
                '--img-height': toCssLength(wrapperStyle?.height ?? style?.height, placeholderHeight),
                ...wrapperStyle,
            } as React.CSSProperties}
        >
            {/* Placeholder dotted box shown while the image is loading */}
            {!loaded && <div className='lazy-image-placeholder' aria-hidden='true' />}

            {/* Spinner overlay — hidden once the image finishes loading */}
            {!loaded && (
                <div className='lazy-image-spinner' aria-hidden='true'>
                    <div className='lazy-image-spinner-circle' />
                </div>
            )}

            {/* lazy-image--loading hides the img until ready; removal lets CSS opacity rules apply normally */}
            <img
                {...imgProps}
                src={src}
                alt={alt ?? ''}
                className={`${className ?? ''}${loaded ? '' : ' lazy-image--loading'}`}
                style={{ width: '100%', height: '100%', objectFit: 'contain', ...style }}
                onLoad={handleLoad}
                onError={handleError}
            />
        </div>
    );
}
